import { validateBiomeBrushSettings as validateGroundBrushSettings } from "./biome-masks.js";
import { canonicalizePaintWeights, paintWeights, samplePaintWeightsBilinear as sampleBilinear } from "./paint-weights.js";
import { WORLD_STUDIO_GROUND_MATERIAL_IDS, WORLD_STUDIO_GROUND_MATERIAL_LAYER_TYPE, WORLD_STUDIO_GROUND_MATERIAL_LAYER_VERSION, isWithinWorldStudioBounds, validateWorldStudioGroundMaterialLayer, validateWorldStudioProject, } from "./project.js";
import { terrainSampleOwner, terrainSamplesPerChunk, worldToTerrainSample } from "./terrain-edits.js";
import { DEFAULT_MAX_TERRAIN_STROKE_SAMPLES, MAX_TERRAIN_STROKE_ELAPSED_SECONDS, TerrainStrokeLimitError, terrainBrushWeight, } from "./terrain-sculpt.js";
export const DEFAULT_GROUND_MATERIAL_LAYER_ID = "ground-material";
export const GROUND_BRUSH_MIN_STRENGTH_PER_SECOND = 0.1;
export const GROUND_BRUSH_MAX_STRENGTH_PER_SECOND = 10;
export const DEFAULT_GROUND_BRUSH_SETTINGS = Object.freeze({ radiusMeters: 12, strengthPerSecond: 2, feather: 0.6 });
const EMPTY_WEIGHTS = Object.freeze([]);
export const worldToGroundSample = worldToTerrainSample;
export const groundSampleOwner = terrainSampleOwner;
/** Sort, drop effectively zero entries and normalize total influence; never invent a default ground. */
const VALID_GROUND_IDS = new Set(WORLD_STUDIO_GROUND_MATERIAL_IDS);
export function canonicalizeGroundWeights(weights, _validIds) {
    for (const [id] of weights)
        if (!VALID_GROUND_IDS.has(id))
            throw new RangeError(`Unsupported ground material: ${id}`);
    return canonicalizePaintWeights(weights, VALID_GROUND_IDS);
}
export function paintGroundWeights(weights, materialId, mode, influence) {
    if (!VALID_GROUND_IDS.has(materialId))
        throw new RangeError(`Unsupported ground material: ${materialId}`);
    return canonicalizeGroundWeights(paintWeights(canonicalizeGroundWeights(weights), materialId, mode, influence));
}
export function createGroundMaterialLayer(id = DEFAULT_GROUND_MATERIAL_LAYER_ID) {
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id) || id.length > 64)
        throw new TypeError("Invalid ground mask layer ID");
    return Object.freeze({ id, type: WORLD_STUDIO_GROUND_MATERIAL_LAYER_TYPE, version: WORLD_STUDIO_GROUND_MATERIAL_LAYER_VERSION, chunks: Object.freeze([]) });
}
export function getGroundWeightsAtSample(layer, grid, sampleX, sampleZ) {
    const owner = terrainSampleOwner(sampleX, sampleZ, terrainSamplesPerChunk(grid));
    return layer.chunks.find(({ x, z }) => x === owner.chunkX && z === owner.chunkZ)
        ?.samples.find(([x, z]) => x === owner.localX && z === owner.localZ)?.[2] ?? EMPTY_WEIGHTS;
}
export function setGroundWeightsAtSample(projectValue, layerValue, sampleX, sampleZ, weights) {
    const project = validateWorldStudioProject(projectValue);
    const layer = validateWorldStudioGroundMaterialLayer(layerValue, project.grid, project.bounds);
    const transaction = new GroundStrokeTransaction({ ...project, layers: [...project.layers.filter(({ id }) => id !== layer.id), layer] }, { layerId: layer.id });
    transaction.setWeights(sampleX, sampleZ, weights);
    return transaction.buildCanonicalLayer();
}
export function removeGroundWeightsAtSample(project, layer, sampleX, sampleZ) {
    return setGroundWeightsAtSample(project, layer, sampleX, sampleZ, EMPTY_WEIGHTS);
}
/** Builds a sparse integer index once. Multiple authored layers combine deterministically, capped at one. */
export function createGroundMaskSampler(projectValue, layerId) {
    const project = validateWorldStudioProject(projectValue);
    const raw = indexRawGroundWeights(project, (layer) => layerId === undefined || layer.id === layerId);
    const index = new Map();
    for (const [sampleKey, values] of raw)
        index.set(sampleKey, normalizeAccumulatedWeights(values));
    return (x, z) => sampleBilinear(project, x, z, (sx, sz) => index.get(key(sx, sz)) ?? EMPTY_WEIGHTS);
}
/** O(nearby samples) writes; canonical arrays are built only when committing a complete stroke. */
export class GroundStrokeTransaction {
    project;
    layerId;
    maxChangedSamples;
    validIds;
    otherLayerValues;
    hasOtherMasks;
    original = new Map();
    values = new Map();
    changes = new Map();
    touched = null;
    status = "active";
    constructor(projectValue, options = {}) {
        this.project = validateWorldStudioProject(projectValue);
        this.layerId = options.layerId ?? DEFAULT_GROUND_MATERIAL_LAYER_ID;
        this.maxChangedSamples = options.maxChangedSamples ?? DEFAULT_MAX_TERRAIN_STROKE_SAMPLES;
        if (!Number.isSafeInteger(this.maxChangedSamples) || this.maxChangedSamples < 1)
            throw new RangeError("Ground stroke limit must be a positive safe integer");
        createGroundMaterialLayer(this.layerId);
        this.validIds = new Set(WORLD_STUDIO_GROUND_MATERIAL_IDS);
        this.hasOtherMasks = this.project.layers.some((layer) => layer.type === WORLD_STUDIO_GROUND_MATERIAL_LAYER_TYPE && layer.id !== this.layerId && layer.chunks.length > 0);
        this.otherLayerValues = indexRawGroundWeights(this.project, (layer) => layer.id !== this.layerId);
        const layer = this.project.layers.find(({ id }) => id === this.layerId);
        if (layer && layer.type !== WORLD_STUDIO_GROUND_MATERIAL_LAYER_TYPE)
            throw new TypeError("Ground layer ID belongs to another layer type");
        const size = terrainSamplesPerChunk(this.project.grid);
        if (layer)
            for (const chunk of layer.chunks)
                for (const [localX, localZ, weights] of chunk.samples) {
                    const entry = Object.freeze({ sampleX: chunk.x * size + localX, sampleZ: chunk.z * size + localZ, weights });
                    this.original.set(key(entry.sampleX, entry.sampleZ), entry);
                    this.values.set(key(entry.sampleX, entry.sampleZ), entry);
                }
    }
    get isActive() { return this.status === "active"; }
    get changedSampleCount() { return this.changes.size; }
    get changedSampleBounds() {
        let bounds = null;
        for (const { sampleX, sampleZ } of this.changes.values())
            bounds = include(bounds, sampleX, sampleZ);
        return bounds;
    }
    get touchedSampleBounds() { return this.touched; }
    getWeights(sampleX, sampleZ) {
        assertCoordinates(sampleX, sampleZ);
        return this.values.get(key(sampleX, sampleZ))?.weights ?? EMPTY_WEIGHTS;
    }
    getOriginalWeights(sampleX, sampleZ) {
        assertCoordinates(sampleX, sampleZ);
        return this.original.get(key(sampleX, sampleZ))?.weights ?? EMPTY_WEIGHTS;
    }
    setWeights(sampleX, sampleZ, weights) {
        if (!this.isActive)
            throw new Error(`Ground transaction is ${this.status}`);
        assertCoordinates(sampleX, sampleZ);
        const spacing = this.project.grid.sampleSpacingMeters;
        if (!isWithinWorldStudioBounds(this.project.bounds, sampleX * spacing, sampleZ * spacing))
            throw new RangeError("Ground sample is outside project authoring bounds");
        const afterWeights = canonicalizeGroundWeights(weights, this.validIds);
        if (equalWeights(afterWeights, this.getWeights(sampleX, sampleZ)))
            return false;
        const beforeWeights = this.getOriginalWeights(sampleX, sampleZ);
        const sampleKey = key(sampleX, sampleZ);
        const reverted = equalWeights(beforeWeights, afterWeights);
        if (!reverted && !this.changes.has(sampleKey) && this.changes.size >= this.maxChangedSamples)
            throw new TerrainStrokeLimitError(this.maxChangedSamples);
        if (afterWeights.length === 0)
            this.values.delete(sampleKey);
        else
            this.values.set(sampleKey, { sampleX, sampleZ, weights: afterWeights });
        if (reverted)
            this.changes.delete(sampleKey);
        else
            this.changes.set(sampleKey, Object.freeze({ sampleX, sampleZ, beforeWeights, afterWeights }));
        this.touched = include(this.touched, sampleX, sampleZ);
        return true;
    }
    sampleWeights(worldX, worldZ) {
        return sampleBilinear(this.project, worldX, worldZ, (x, z) => this.status === "cancelled" ? this.getOriginalWeights(x, z) : this.getWeights(x, z));
    }
    /** Full live mask, including other immutable layers, composed on the lattice before interpolation. */
    sampleProjectWeights(worldX, worldZ) {
        if (!this.hasOtherMasks)
            return this.sampleWeights(worldX, worldZ);
        return sampleBilinear(this.project, worldX, worldZ, (x, z) => {
            const values = new Map(this.otherLayerValues.get(key(x, z)));
            const current = this.status === "cancelled" ? this.getOriginalWeights(x, z) : this.getWeights(x, z);
            for (const [id, weight] of current)
                values.set(id, (values.get(id) ?? 0) + weight);
            return normalizeAccumulatedWeights(values);
        });
    }
    sampleOriginalWeights(worldX, worldZ) {
        return sampleBilinear(this.project, worldX, worldZ, (x, z) => this.getOriginalWeights(x, z));
    }
    buildCanonicalLayer() {
        const size = terrainSamplesPerChunk(this.project.grid);
        const chunks = new Map();
        for (const { sampleX, sampleZ, weights } of this.values.values()) {
            const owner = terrainSampleOwner(sampleX, sampleZ, size);
            const chunkKey = key(owner.chunkX, owner.chunkZ);
            let chunk = chunks.get(chunkKey);
            if (!chunk) {
                chunk = { x: owner.chunkX, z: owner.chunkZ, samples: [] };
                chunks.set(chunkKey, chunk);
            }
            chunk.samples.push([owner.localX, owner.localZ, weights]);
        }
        const ordered = [...chunks.values()].sort((a, b) => a.z - b.z || a.x - b.x);
        for (const chunk of ordered)
            chunk.samples.sort((a, b) => a[1] - b[1] || a[0] - b[0]);
        return validateWorldStudioGroundMaterialLayer({ ...createGroundMaterialLayer(this.layerId), chunks: ordered }, this.project.grid, this.project.bounds);
    }
    commit(mode) {
        if (!this.isActive)
            throw new Error(`Ground transaction is ${this.status}`);
        assertMode(mode);
        if (!this.changes.size) {
            this.status = "committed";
            return null;
        }
        const layer = this.buildCanonicalLayer();
        const existing = this.project.layers.some(({ id }) => id === this.layerId);
        const layers = existing ? this.project.layers.map((current) => current.id === this.layerId ? layer : current) : [...this.project.layers, layer];
        const project = validateWorldStudioProject({ ...this.project, revision: this.project.revision + 1, layers });
        const command = Object.freeze({ type: "ground-material-stroke", mode, layerId: this.layerId, changes: Object.freeze([...this.changes.values()].sort((a, b) => a.sampleZ - b.sampleZ || a.sampleX - b.sampleX)) });
        this.status = "committed";
        return Object.freeze({ project, layer, command, changedSampleBounds: this.changedSampleBounds });
    }
    cancel() { if (this.isActive)
        this.status = "cancelled"; return this.project; }
}
export { validateBiomeBrushSettings as validateGroundBrushSettings } from "./biome-masks.js";
export function applyGroundStrokeStamp(options) {
    const { transaction, materialId, mode, stamp } = options;
    const settings = validateGroundBrushSettings(options.settings);
    assertMode(mode);
    if (!WORLD_STUDIO_GROUND_MATERIAL_IDS.includes(materialId))
        throw new RangeError(`Unsupported ground material: ${materialId}`);
    if (![stamp.centerX, stamp.centerZ, stamp.elapsedSeconds].every(Number.isFinite))
        throw new RangeError("Ground stamp must be finite");
    const elapsed = Math.max(0, Math.min(MAX_TERRAIN_STROKE_ELAPSED_SECONDS, stamp.elapsedSeconds));
    if (elapsed === 0)
        return Object.freeze({ changedSampleCount: 0, changedSampleBounds: null, heightChanged: false });
    const spacing = transaction.project.grid.sampleSpacingMeters;
    const radius = settings.radiusMeters;
    let changedSampleCount = 0;
    let changedSampleBounds = null;
    const minX = Math.ceil((stamp.centerX - radius) / spacing), maxX = Math.floor((stamp.centerX + radius) / spacing);
    const minZ = Math.ceil((stamp.centerZ - radius) / spacing), maxZ = Math.floor((stamp.centerZ + radius) / spacing);
    if (![minX, maxX, minZ, maxZ].every(Number.isSafeInteger))
        throw new RangeError("Ground stamp sample bounds must be safe integers");
    for (let z = minZ; z <= maxZ; z += 1) {
        for (let x = minX; x <= maxX; x += 1) {
            if (!isWithinWorldStudioBounds(transaction.project.bounds, x * spacing, z * spacing))
                continue;
            const weight = terrainBrushWeight(Math.hypot(x * spacing - stamp.centerX, z * spacing - stamp.centerZ), radius, settings.feather);
            if (weight === 0)
                continue;
            // Exponential relaxation is invariant under time subdivision and divided interpolation stamps.
            const influence = -Math.expm1(-settings.strengthPerSecond * elapsed * weight);
            const before = transaction.getWeights(x, z);
            const after = paintGroundWeights(before, materialId, mode, influence);
            if (transaction.setWeights(x, z, after)) {
                changedSampleCount += 1;
                changedSampleBounds = include(changedSampleBounds, x, z);
            }
        }
    }
    return Object.freeze({ changedSampleCount, changedSampleBounds, heightChanged: false });
}
export function applyGroundHistoryCommand(projectValue, command, direction) {
    if (!command || command.type !== "ground-material-stroke" || !Array.isArray(command.changes))
        throw new TypeError("Invalid ground history command");
    if (direction !== "undo" && direction !== "redo")
        throw new RangeError("Ground history direction must be undo or redo");
    assertMode(command.mode);
    const transaction = new GroundStrokeTransaction(projectValue, { layerId: command.layerId, maxChangedSamples: Math.max(1, command.changes.length) });
    const seen = new Set();
    for (const change of command.changes) {
        assertCoordinates(change.sampleX, change.sampleZ);
        const sampleKey = key(change.sampleX, change.sampleZ);
        if (seen.has(sampleKey))
            throw new RangeError("Duplicate ground history sample");
        seen.add(sampleKey);
        canonicalizeGroundWeights(change.beforeWeights);
        canonicalizeGroundWeights(change.afterWeights);
        transaction.setWeights(change.sampleX, change.sampleZ, direction === "undo" ? change.beforeWeights : change.afterWeights);
    }
    const result = transaction.commit(command.mode);
    return result ? Object.freeze({ project: result.project, command, changedSampleBounds: result.changedSampleBounds }) : null;
}
/** Raw cross-layer sums stay transient until all layers have contributed. */
function indexRawGroundWeights(project, includeLayer) {
    const index = new Map();
    const size = terrainSamplesPerChunk(project.grid);
    for (const layer of [...project.layers].sort((left, right) => left.id < right.id ? -1 : left.id > right.id ? 1 : 0)) {
        if (layer.type !== WORLD_STUDIO_GROUND_MATERIAL_LAYER_TYPE || !includeLayer(layer))
            continue;
        for (const chunk of layer.chunks)
            for (const [localX, localZ, weights] of chunk.samples) {
                const sampleKey = key(chunk.x * size + localX, chunk.z * size + localZ);
                let values = index.get(sampleKey);
                if (!values) {
                    values = new Map();
                    index.set(sampleKey, values);
                }
                for (const [id, weight] of weights)
                    values.set(id, (values.get(id) ?? 0) + weight);
            }
    }
    return index;
}
function normalizeAccumulatedWeights(values) {
    const ordered = [...values].sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0);
    const sum = ordered.reduce((total, [, weight]) => total + weight, 0);
    return canonicalizeGroundWeights(ordered.map(([id, weight]) => [id, weight / Math.max(1, sum)]));
}
function key(x, z) { return `${x}:${z}`; }
function assertCoordinates(x, z) {
    if (!Number.isSafeInteger(x) || !Number.isSafeInteger(z))
        throw new RangeError("Ground sample coordinates must be safe integers");
}
function assertMode(mode) { if (mode !== "replace" && mode !== "erase")
    throw new RangeError("Unsupported ground paint mode"); }
function equalWeights(a, b) { return a.length === b.length && a.every(([id, weight], index) => id === b[index][0] && weight === b[index][1]); }
function include(bounds, x, z) {
    return Object.freeze({ minSampleX: Math.min(bounds?.minSampleX ?? x, x), minSampleZ: Math.min(bounds?.minSampleZ ?? z, z), maxSampleX: Math.max(bounds?.maxSampleX ?? x, x), maxSampleZ: Math.max(bounds?.maxSampleZ ?? z, z) });
}
